import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, RefreshCw, Download, Eye, CheckCircle2, XCircle, ToggleLeft, ToggleRight } from "lucide-react";
import { currentBs, lastNBsMonths, bsMonthName, BS_MONTH_NAMES } from "@/lib/bs-calendar";

/**
 * Phase 6.1.D — Dealer Sell-Out Schemes (PM proposes → Admin/Service Head approves).
 * Provides a maker-checker UI, a dealer-wise qualification ledger, and Excel export.
 */
export default function SellOutSchemes() {
  const { user } = useAuth();
  const role = user?.role;
  const canPropose = ["admin", "product_manager"].includes(role);
  const canDecide = ["admin", "service_head"].includes(role);
  const canToggle = role === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(""); // all / pending / approved / rejected
  const [showCreate, setShowCreate] = useState(false);
  const [qualifyFor, setQualifyFor] = useState(null);
  const [decisionFor, setDecisionFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/promoter/schemes", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load schemes", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  return (
    <div className="space-y-4" data-testid="schemes-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Dealer Sell-Out Schemes</h1>
          <p className="text-sm text-slate-500 mt-1">
            Additive incentives for dealers (and optionally self-promoters) calculated on{" "}
            <span className="font-medium text-slate-700">net sales (excluding 13% VAT)</span>{" "}
            within a BS period. Regular promoter calculations are unaffected.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Status</Label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-8 w-36 rounded-md border border-slate-200 px-2 text-sm bg-white"
              data-testid="schemes-status-filter">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <Button variant="outline" onClick={load} data-testid="schemes-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          {canPropose && (
            <Button onClick={() => setShowCreate(true)} data-testid="schemes-create-btn">
              <Plus size={14} className="mr-1" /> New Scheme
            </Button>
          )}
        </div>
      </header>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <table className="w-full text-sm" data-testid="schemes-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Period</th>
                <th className="text-left px-3 py-2">Eligible</th>
                <th className="text-left px-3 py-2">Qualification</th>
                <th className="text-left px-3 py-2">Benefit</th>
                <th className="text-left px-3 py-2">Applies&nbsp;to</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Active</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500" data-testid="schemes-empty">
                  No schemes yet.
                </td></tr>
              )}
              {rows.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`scheme-row-${s.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{s.name}</td>
                  <td className="px-3 py-2">{periodText(s)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {s.eligible_brand || "Any"} / {s.eligible_category || "Any"}
                    {s.eligible_product_ids?.length ? ` · ${s.eligible_product_ids.length} sku` : ""}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {s.qualification_kind === "min_units"
                      ? `≥ ${s.min_units} units`
                      : `≥ Rs. ${Number(s.min_net_sales || 0).toLocaleString()} (net)`}
                  </td>
                  <td className="px-3 py-2 text-xs">{benefitText(s)}</td>
                  <td className="px-3 py-2 text-xs capitalize">{s.applies_to}</td>
                  <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                  <td className="px-3 py-2 text-xs">
                    {s.status === "approved" ? (s.active ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge variant="outline">Off</Badge>) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setQualifyFor(s)} data-testid={`scheme-view-${s.id}`}>
                        <Eye size={14} className="mr-1" /> Ledger
                      </Button>
                      {canDecide && s.status === "pending" && (
                        <Button size="sm" variant="outline" onClick={() => setDecisionFor(s)}
                                data-testid={`scheme-decide-${s.id}`}>
                          Decide
                        </Button>
                      )}
                      {canToggle && s.status === "approved" && (
                        <Button size="sm" variant="ghost"
                                onClick={() => toggleActive(s, load)}
                                data-testid={`scheme-toggle-${s.id}`}>
                          {s.active ? <ToggleRight size={14} className="text-emerald-600" /> : <ToggleLeft size={14} />}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <SchemeCreateDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
      <QualificationDialog scheme={qualifyFor} onClose={() => setQualifyFor(null)} />
      <DecisionDialog scheme={decisionFor} onClose={() => setDecisionFor(null)} onDecided={load} />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
  };
  return <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${map[status] || "bg-slate-100 text-slate-700"}`}>{status}</span>;
}

function periodText(s) {
  if (s.period_kind === "bs_month") {
    const [y, m] = String(s.bs_month_year || "").split("-");
    return `${bsMonthName(Number(m))} ${y} (BS)`;
  }
  if (s.period_kind === "bs_quarter") return s.bs_quarter_key;
  if (s.period_kind === "bs_range") return `${s.bs_from} → ${s.bs_to}`;
  return "—";
}

function benefitText(s) {
  if (s.benefit_kind === "fixed_per_unit") return `Rs. ${Number(s.benefit_value).toLocaleString()}/unit`;
  if (s.benefit_kind === "fixed_total") return `Rs. ${Number(s.benefit_value).toLocaleString()} flat`;
  if (s.benefit_kind === "pct_of_net_sales") return `${s.benefit_value}% of net sales`;
  return "—";
}

async function toggleActive(s, reload) {
  try {
    await api.patch(`/promoter/schemes/${s.id}/active`, { active: !s.active });
    toast.success(`${!s.active ? "Activated" : "Deactivated"} scheme`);
    reload();
  } catch (e) {
    toast.error("Could not toggle", { description: formatApiErrorDetail(e.response?.data?.detail) });
  }
}

function SchemeCreateDialog({ open, onClose, onCreated }) {
  const nowBs = useMemo(() => currentBs(), []);
  const bsOptions = useMemo(
    () => (nowBs ? lastNBsMonths(13, nowBs.bs_year + 1, nowBs.bs_month) : []),
    [nowBs],
  );
  const [form, setForm] = useState({
    name: "", description: "",
    period_kind: "bs_month",
    bs_month_year: bsOptions[0]?.value || "",
    bs_quarter_key: "",
    bs_from: "", bs_to: "",
    eligible_brand: "", eligible_category: "",
    qualification_kind: "min_units",
    min_units: 5, min_net_sales: 0,
    benefit_kind: "fixed_per_unit",
    benefit_value: 500,
    self_promoter_entry_incentive: 0,
    applies_to: "dealer",
  });
  useEffect(() => {
    if (open && bsOptions.length && !form.bs_month_year) {
      setForm((f) => ({ ...f, bs_month_year: bsOptions[bsOptions.length - 1].value }));
    }
  }, [open, bsOptions]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    try {
      const payload = { ...form };
      if (payload.qualification_kind === "min_units") payload.min_units = Number(payload.min_units || 0);
      else payload.min_net_sales = Number(payload.min_net_sales || 0);
      payload.benefit_value = Number(payload.benefit_value || 0);
      if (["self_promoter", "both"].includes(payload.applies_to))
        payload.self_promoter_entry_incentive = Number(payload.self_promoter_entry_incentive || 0);
      await api.post("/promoter/schemes", payload);
      toast.success("Scheme proposed for approval");
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error("Could not create", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="scheme-create-dialog">
        <DialogHeader>
          <DialogTitle>New Sell-Out Scheme</DialogTitle>
          <DialogDescription>Propose a sell-out scheme for approval. PM/Admin can propose; Service Head/Admin approves.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="scheme-name" />
          </Field>
          <Field label="Applies to">
            <select value={form.applies_to} onChange={(e) => set("applies_to", e.target.value)}
                    className="h-9 w-full rounded-md border border-slate-200 px-2"
                    data-testid="scheme-applies-to">
              <option value="dealer">Dealer only</option>
              <option value="self_promoter">Self promoter only</option>
              <option value="both">Both</option>
            </select>
          </Field>
          <Field label="Description" col2>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)} />
          </Field>
          <Field label="Period kind">
            <select value={form.period_kind} onChange={(e) => set("period_kind", e.target.value)}
                    className="h-9 w-full rounded-md border border-slate-200 px-2"
                    data-testid="scheme-period-kind">
              <option value="bs_month">BS month</option>
              <option value="bs_quarter">BS quarter (FY)</option>
              <option value="bs_range">BS date range</option>
            </select>
          </Field>
          {form.period_kind === "bs_month" && (
            <Field label="BS Month">
              <select value={form.bs_month_year} onChange={(e) => set("bs_month_year", e.target.value)}
                      className="h-9 w-full rounded-md border border-slate-200 px-2"
                      data-testid="scheme-bs-month">
                {bsOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          )}
          {form.period_kind === "bs_quarter" && (
            <Field label="BS Quarter Key (YYYY-Q#)">
              <Input value={form.bs_quarter_key} onChange={(e) => set("bs_quarter_key", e.target.value)}
                     placeholder="e.g. 2082-Q4" data-testid="scheme-bs-quarter" />
            </Field>
          )}
          {form.period_kind === "bs_range" && (
            <>
              <Field label="BS From (YYYY-MM-DD)">
                <Input value={form.bs_from} onChange={(e) => set("bs_from", e.target.value)} placeholder="2083-03-01" />
              </Field>
              <Field label="BS To (YYYY-MM-DD)">
                <Input value={form.bs_to} onChange={(e) => set("bs_to", e.target.value)} placeholder="2083-03-31" />
              </Field>
            </>
          )}
          <Field label="Eligible Brand (optional)">
            <Input value={form.eligible_brand} onChange={(e) => set("eligible_brand", e.target.value)} placeholder="e.g. HISENSE" />
          </Field>
          <Field label="Eligible Category (optional)">
            <Input value={form.eligible_category} onChange={(e) => set("eligible_category", e.target.value)} placeholder="e.g. TV" />
          </Field>
          <Field label="Qualification">
            <select value={form.qualification_kind} onChange={(e) => set("qualification_kind", e.target.value)}
                    className="h-9 w-full rounded-md border border-slate-200 px-2"
                    data-testid="scheme-qual-kind">
              <option value="min_units">Min units</option>
              <option value="min_net_sales">Min net sales (NPR)</option>
            </select>
          </Field>
          {form.qualification_kind === "min_units" ? (
            <Field label="Min units">
              <Input type="number" value={form.min_units} onChange={(e) => set("min_units", e.target.value)} />
            </Field>
          ) : (
            <Field label="Min net sales (NPR, excl. VAT)">
              <Input type="number" value={form.min_net_sales} onChange={(e) => set("min_net_sales", e.target.value)} />
            </Field>
          )}
          <Field label="Benefit kind">
            <select value={form.benefit_kind} onChange={(e) => set("benefit_kind", e.target.value)}
                    className="h-9 w-full rounded-md border border-slate-200 px-2">
              <option value="fixed_per_unit">Fixed per unit</option>
              <option value="fixed_total">Fixed total (flat)</option>
              <option value="pct_of_net_sales">% of net sales</option>
            </select>
          </Field>
          <Field label="Benefit value">
            <Input type="number" value={form.benefit_value} onChange={(e) => set("benefit_value", e.target.value)} data-testid="scheme-benefit-value" />
          </Field>
          {["self_promoter", "both"].includes(form.applies_to) && (
            <Field label="Self-promoter entry incentive (NPR)">
              <Input type="number" value={form.self_promoter_entry_incentive}
                     onChange={(e) => set("self_promoter_entry_incentive", e.target.value)} />
            </Field>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="scheme-submit">Propose</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, col2, children }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase font-semibold text-slate-600">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}

function DecisionDialog({ scheme, onClose, onDecided }) {
  const [decision, setDecision] = useState("approved");
  const [remarks, setRemarks] = useState("");
  if (!scheme) return null;
  const submit = async () => {
    try {
      await api.patch(`/promoter/schemes/${scheme.id}/decide`, { decision, remarks });
      toast.success(`Scheme ${decision}`);
      onDecided && onDecided();
      onClose();
    } catch (e) {
      toast.error("Could not decide", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };
  return (
    <Dialog open={!!scheme} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="scheme-decide-dialog">
        <DialogHeader><DialogTitle>Decide · {scheme.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2">
            <Button variant={decision === "approved" ? "default" : "outline"}
                    onClick={() => setDecision("approved")} data-testid="decide-approve">
              <CheckCircle2 size={14} className="mr-1" /> Approve
            </Button>
            <Button variant={decision === "rejected" ? "default" : "outline"}
                    onClick={() => setDecision("rejected")} data-testid="decide-reject">
              <XCircle size={14} className="mr-1" /> Reject
            </Button>
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Remarks (optional)</Label>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="decide-remarks" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="decide-submit">Submit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QualificationDialog({ scheme, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!scheme) return;
    setData(null);
    api.get(`/promoter/schemes/${scheme.id}/qualification`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error("Could not load ledger", { description: formatApiErrorDetail(e.response?.data?.detail) }));
  }, [scheme]);
  if (!scheme) return null;
  const exportXlsx = () => {
    const token = localStorage.getItem("access_token");
    fetch(`${API}/promoter/schemes/${scheme.id}/qualification.xlsx`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => { if (!r.ok) throw new Error("export"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `scheme_${scheme.name.replace(/\s+/g, "_")}_ledger.xlsx`; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error("Could not export"));
  };
  return (
    <Dialog open={!!scheme} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="scheme-qualification-dialog">
        <DialogHeader>
          <DialogTitle>{scheme.name} · Dealer Qualification</DialogTitle>
        </DialogHeader>
        <div className="flex justify-between items-center mb-2">
          <div className="text-xs text-slate-500">
            Ledger only — Finance settles separately. Net sales exclude 13% VAT.
          </div>
          <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="scheme-export-xlsx">
            <Download size={14} className="mr-1" /> Export
          </Button>
        </div>
        {!data && <div className="text-sm text-slate-500">Loading…</div>}
        {data && (
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2 text-xs">
              <Tile label="Dealers" value={data.totals?.dealers || 0} />
              <Tile label="Qualified" value={data.totals?.qualified_dealers || 0} />
              <Tile label="Total dealer benefit" value={`Rs. ${(data.totals?.total_dealer_benefit || 0).toLocaleString()}`} />
              <Tile label="Total self-promoter payout" value={`Rs. ${(data.totals?.total_self_promoter_payout || 0).toLocaleString()}`} />
            </div>
            <div className="overflow-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Dealer</th>
                    <th className="text-right px-2 py-1">Units</th>
                    <th className="text-right px-2 py-1">Net Sales</th>
                    <th className="text-left px-2 py-1">Qualified</th>
                    <th className="text-right px-2 py-1">Dealer Benefit</th>
                    <th className="text-right px-2 py-1">SP Count</th>
                    <th className="text-right px-2 py-1">SP Entry Total</th>
                    <th className="text-right px-2 py-1">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.dealer_id} className={`border-t border-slate-100 ${r.qualified ? "" : "text-slate-400"}`}>
                      <td className="px-2 py-1">{r.dealer_name}</td>
                      <td className="px-2 py-1 text-right">{r.units}</td>
                      <td className="px-2 py-1 text-right">{Number(r.net_sales || 0).toLocaleString()}</td>
                      <td className="px-2 py-1">{r.qualified ? "Yes" : "No"}</td>
                      <td className="px-2 py-1 text-right font-semibold">{Number(r.dealer_benefit || 0).toLocaleString()}</td>
                      <td className="px-2 py-1 text-right">{r.self_promoter_count || 0}</td>
                      <td className="px-2 py-1 text-right">{Number(r.self_promoter_entry_total || 0).toLocaleString()}</td>
                      <td className="px-2 py-1 text-right font-semibold">{Number(r.total_payout || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Tile({ label, value }) {
  return (
    <div className="bg-slate-50 rounded p-2">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}
